import {
  GAME_MOVES_KEY,
  GAME_SECRET_KEY,
  deriveBattleship,
  deriveBeerPong,
  deriveCheckers,
  deriveConnect4,
  deriveMancala,
  derivePool,
  gameDocName,
  gameUserDocName,
  type BattleshipMove,
  type BeerPongMove,
  type BattleshipState,
  type CheckersMove,
  type Connect4Move,
  type MancalaMove,
  type MancalaState,
  type PoolMove,
  type Connect4State,
  type GameMeta,
  type ReplayableMove,
} from '@awwyeah/shared';
import type * as Y from 'yjs';
import { useYArray, useYDoc, useYMap } from '../../sync';
import type { BattleshipSecret } from './battleshipOps';
import { latestMoveAt } from './listOrder';

/**
 * A match's move log (`game:<id>`, an append-only `Y.Array`) plus the derived state — the
 * append-only representation is the CRDT-friendly one (ROADMAP §3 WS-17): the board itself
 * is never stored, only ever replayed. Offline, a client keeps appending its own moves to
 * its local copy of this array; when it reconnects, Yjs merges the arrays and the same
 * `derive` call re-derives whatever the merged order actually allows — including silently
 * dropping a move that turned out to be a stray/duplicate/out-of-turn one (see
 * `packages/shared/src/modules/games/connect4.ts`).
 *
 * One hook per kind rather than a generic one: the move *type* and the state type differ,
 * and a union-typed hook would push a cast into every caller for no benefit. The shared
 * `game:<id>` doc is family-visible either way — shots, results and end-of-game reveals are
 * public, because spectating is the point.
 */
export function useConnect4Moves(
  gameId: string,
  playerIds: readonly [string, string],
): {
  doc: Y.Doc;
  movesArray: Y.Array<Connect4Move>;
  moves: Connect4Move[];
  state: Connect4State;
} {
  const doc = useYDoc(gameDocName(gameId));
  const { array, items } = useYArray<Connect4Move>(doc, GAME_MOVES_KEY);
  const state = deriveConnect4(items, playerIds);
  return { doc, movesArray: array, moves: items, state };
}

/** Mancala's move log + derived board. */
export function useMancalaMoves(
  gameId: string,
  playerIds: readonly [string, string],
): {
  movesArray: Y.Array<MancalaMove>;
  moves: MancalaMove[];
  state: MancalaState;
} {
  const doc = useYDoc(gameDocName(gameId));
  const { array, items } = useYArray<MancalaMove>(doc, GAME_MOVES_KEY);
  const state = deriveMancala(items, playerIds);
  return { movesArray: array, moves: items, state };
}

/** Battleship's half of the same shared log: commits, shots, resolutions, reveals. */
export function useBattleshipMoves(
  gameId: string,
  playerIds: readonly [string, string],
): {
  doc: Y.Doc;
  movesArray: Y.Array<BattleshipMove>;
  moves: BattleshipMove[];
  state: BattleshipState;
} {
  const doc = useYDoc(gameDocName(gameId));
  const { array, items } = useYArray<BattleshipMove>(doc, GAME_MOVES_KEY);
  const state = deriveBattleship(items, playerIds);
  return { doc, movesArray: array, moves: items, state };
}

/**
 * What the Apps-tab list needs to know about a match without caring which kind it is: how
 * many moves, whether it's over, and whether the viewer is the one holding the turn.
 * Kind-aware in one place so `GamesSection` stays kind-agnostic.
 *
 * For Battleship, `isMyTurn` is false while a shot awaits the defender's device — the game
 * genuinely isn't waiting on either player then, and a "your move" badge in that gap would
 * be asking someone for something the app hasn't finished doing yet.
 */
export interface GameSummary {
  moves: ReplayableMove[];
  isMyTurn: boolean;
  finished: boolean;
  winnerIndex: 0 | 1 | null;
  /** Newest client-stamped move time — what the list sorts and folds "Quiet games" by. */
  lastMoveAt: string | null;
  /** A kind-specific word for the viewer's side beyond their colour (Pool's "Solids"), or
   * `null` where the colour says it all. */
  sideDetail: string | null;
}

export function useGameSummary(game: GameMeta, meId: string | null): GameSummary {
  const doc = useYDoc(gameDocName(game.id));
  const { items } = useYArray<unknown>(doc, GAME_MOVES_KEY);
  const lastMoveAt = latestMoveAt(items);
  const meIndex = meId === null ? -1 : game.playerIds.indexOf(meId);

  if (game.kind === 'battleship') {
    const state = deriveBattleship(items as BattleshipMove[], game.playerIds);
    return {
      moves: items as ReplayableMove[],
      isMyTurn: state.turn !== null && game.playerIds[state.turn] === meId,
      finished: state.phase === 'done',
      winnerIndex: state.winner,
      lastMoveAt,
      sideDetail: null,
    };
  }
  if (game.kind === 'beerpong') {
    const state = deriveBeerPong(items as BeerPongMove[], game.playerIds);
    return {
      moves: items as ReplayableMove[],
      isMyTurn: state.turn !== null && game.playerIds[state.turn] === meId,
      finished: state.winner !== null,
      winnerIndex: state.winner,
      lastMoveAt,
      sideDetail: null,
    };
  }
  if (game.kind === 'pool') {
    const state = derivePool(items as PoolMove[], game.playerIds);
    const group = meIndex === -1 ? null : state.groups[meIndex as 0 | 1];
    return {
      moves: items as ReplayableMove[],
      isMyTurn: state.turn !== null && game.playerIds[state.turn] === meId,
      finished: state.winner !== null,
      winnerIndex: state.winner,
      lastMoveAt,
      sideDetail: group === 'solids' ? 'Solids' : group === 'stripes' ? 'Stripes' : null,
    };
  }
  if (game.kind === 'mancala') {
    const state = deriveMancala(items as MancalaMove[], game.playerIds);
    return {
      moves: items as ReplayableMove[],
      isMyTurn: state.turn !== null && game.playerIds[state.turn] === meId,
      finished: state.winner !== null || state.isDraw,
      winnerIndex: state.winner,
      lastMoveAt,
      sideDetail: null,
    };
  }
  if (game.kind === 'checkers') {
    const state = deriveCheckers(items as CheckersMove[], game.playerIds);
    return {
      moves: items as ReplayableMove[],
      isMyTurn: state.turn !== null && game.playerIds[state.turn] === meId,
      finished: state.winner !== null || state.isDraw,
      winnerIndex: state.winner,
      lastMoveAt,
      sideDetail: null,
    };
  }
  const state = deriveConnect4(items as Connect4Move[], game.playerIds);
  return {
    moves: items as ReplayableMove[],
    isMyTurn: state.turn !== null && game.playerIds[state.turn] === meId,
    finished: state.winner !== null || state.isDraw,
    winnerIndex: state.winner?.playerIndex ?? null,
    lastMoveAt,
    sideDetail: null,
  };
}

/**
 * **The hidden half.** `game:<id>:user:<userId>` holds one player's ship placement and the
 * salt behind their public commit hash. Nobody else can open it: the sync hub refuses the
 * upgrade with a 403 based on the doc *name* alone, with no ACL row involved and none able
 * to widen it (`@awwyeah/shared`'s `personalDocOwnerId`, proven in
 * `apps/server/test/sharing/authorize.test.ts`).
 *
 * Always called with the **signed-in** user's own id, including for spectators — a viewer
 * opening their own (empty) doc for a game they aren't in is harmless, and it keeps the hook
 * order unconditional. It is never, under any circumstance, called with the opponent's id:
 * that request would 403, and asking at all would mean the client believed it might work.
 */
export function useGameSecret(
  gameId: string,
  userId: string,
): {
  secret: BattleshipSecret | null;
  save: (secret: BattleshipSecret) => void;
} {
  const doc = useYDoc(gameUserDocName(gameId, userId));
  const { map, entries } = useYMap<unknown>(doc, GAME_SECRET_KEY);

  const stored = Object.fromEntries(entries) as Partial<BattleshipSecret>;
  const secret =
    stored.placement && typeof stored.salt === 'string'
      ? { placement: stored.placement, salt: stored.salt }
      : null;

  const save = (next: BattleshipSecret): void => {
    doc.transact(() => {
      map.set('placement', next.placement);
      map.set('salt', next.salt);
    });
  };

  return { secret, save };
}
